import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useContent } from '../../lib/content';
import { pickLocale, API_BASE } from '../../lib/api';
import ResponsiveImage from './ResponsiveImage';
import { GALLERY_SIZES, HALF_SIZES } from '../../lib/responsive';

export default function Showcase() {
  const { content, locale } = useContent();
  const data = content.showcase || {};
  const [pct, setPct] = useState(50);
  const [drag, setDrag] = useState(false);
  const [mediaGallery, setMediaGallery] = useState([]);
  const ref = useRef(null);

  // Media Library fallback: when the CMS gallery is empty, pull assets
  // tagged with section_hint 'showcase.gallery' directly from the library.
  useEffect(() => {
    if (data.gallery?.length > 0) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/media/by-section/showcase.gallery?limit=8`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        // Mantén el objeto entero (incluye variants + variants_avif) para responsive srcset.
        const items = (d.items || []).map((a) => ({
          src: a.thumb_url || a.url,
          variants: a.variants,
          variantsAvif: a.variants_avif,
        }));
        setMediaGallery(items);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [data.gallery]);

  // Si la CMS gallery viene rellena (strings), envuelve cada elemento en {src}.
  const cmsGallery = Array.isArray(data.gallery)
    ? data.gallery.map((s) => (typeof s === 'string' ? { src: s } : s))
    : [];
  const gallery = cmsGallery.length > 0 ? cmsGallery : mediaGallery;

  const move = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = clientX - r.left;
    setPct(Math.max(2, Math.min(98, (x / r.width) * 100)));
  };

  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="section bg-bg"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-20">
          <div>
            <p className="text-eyebrow mb-4" data-testid="showcase-eyebrow">{pickLocale(data.eyebrow, locale)}</p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-ink max-w-2xl" data-testid="showcase-title">
              {pickLocale(data.title, locale)}
            </h2>
          </div>
          <p className="md:max-w-md text-ink-muted text-base leading-relaxed">
            {pickLocale(data.subtitle, locale)}
          </p>
        </div>

        {/* Drag-to-reveal — vertical aspect (3:4) + object-contain so the FULL
            editorial image is visible without cropping heads/feet. */}
        <div
          ref={ref}
          data-testid="showcase-slider"
          className="relative w-full max-w-2xl mx-auto aspect-[3/4] overflow-hidden rounded-md border border-line bg-bg-secondary select-none"
          onMouseDown={(e) => { setDrag(true); move(e.clientX); }}
          onMouseMove={(e) => drag && move(e.clientX)}
          onMouseUp={() => setDrag(false)}
          onMouseLeave={() => setDrag(false)}
          onTouchStart={(e) => { setDrag(true); move(e.touches[0].clientX); }}
          onTouchMove={(e) => drag && move(e.touches[0].clientX)}
          onTouchEnd={() => setDrag(false)}
        >
          {data.before_image && (
            <ResponsiveImage
              src={data.before_image}
              alt="before"
              draggable={false}
              loading="lazy"
              sizes={HALF_SIZES}
              className="absolute inset-0 w-full h-full object-contain"
            />
          )}
          {data.after_image && (
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${pct}%` }}
            >
              <div className="absolute inset-0 bg-bg-secondary" style={{ width: `${(100 / pct) * 100}%`, maxWidth: 'none' }}>
                <ResponsiveImage
                  src={data.after_image}
                  alt="after"
                  draggable={false}
                  loading="lazy"
                  sizes={HALF_SIZES}
                  className="absolute inset-0 w-full h-full object-contain"
                />
              </div>
            </div>
          )}

          <div className="dragline" style={{ left: `calc(${pct}% - 1px)` }}>
            <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-11 h-11 rounded-full border border-acid bg-acid/20 backdrop-blur-sm flex items-center justify-center">
              <span className="text-acid text-lg font-mono">⇆</span>
            </div>
          </div>

          {/* Tags */}
          <div className="absolute top-4 left-4 text-eyebrow text-ink/80 backdrop-blur-md bg-bg/40 px-3 py-1.5 rounded-full border border-line">
            {locale === 'es' ? 'Antes' : 'Before'}
          </div>
          <div className="absolute top-4 right-4 text-eyebrow text-acid backdrop-blur-md bg-bg/40 px-3 py-1.5 rounded-full border border-acid/40">
            {locale === 'es' ? 'Generado · MODARIA' : 'Generated · MODARIA'}
          </div>
        </div>

        {/* Mini gallery */}
        {gallery?.length > 0 && (
          <div className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5" data-testid="showcase-gallery">
            {gallery.slice(0, 8).map((item, i) => {
              let raw = null;
              let variants;
              let variantsAvif;
              if (typeof item === 'string') {
                raw = item;
              } else if (item && typeof item === 'object') {
                raw = typeof item.src === 'string' ? item.src : null;
                variants = item.variants;
                variantsAvif = item.variantsAvif;
              }
              if (typeof raw !== 'string' || !raw) return null;
              const full = raw.startsWith('http') ? raw : `${API_BASE}${raw}`;
              return (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  className="aspect-[3/4] overflow-hidden rounded-sm border border-line"
                >
                  <ResponsiveImage
                    src={full}
                    variants={variants}
                    variantsAvif={variantsAvif}
                    alt={`gen ${i}`}
                    sizes={GALLERY_SIZES}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
